import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { wsService, WsMessage } from '../services/websocket'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { useUrlParams } from '../hooks/useUrlParams'

interface SessionFeatures {
  storyteller?: boolean
  [key: string]: boolean | undefined
}

interface WebSocketContextType {
  connected: boolean
  sessionId: string
  /** Set when the server handed back a DIFFERENT session than the one asked
   *  for — it holds the id that was REQUESTED. The server forks rather than
   *  joins when the requested session exists but its character / story /
   *  features differ from this connect's, and the fork is otherwise invisible:
   *  the new id lands in `sessionId` and is written into the page URL, so this
   *  UI looks connected while the other clients on the requested session never
   *  hear a thing. Null on a normal join. */
  forkedFrom: string | null
  sessionFeatures: SessionFeatures
  sessionCharacter: string
  sessionStory: string
  sessionMode: string
  sessionTransmission: string
  send: (msg: object) => void
  onMessage: (type: string, handler: (msg: WsMessage) => void) => () => void
}

const WebSocketContext = createContext<WebSocketContextType>(null!)

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false)
  const [sessionId, setSessionId] = useLocalStorage('sessionId', '')
  const [forkedFrom, setForkedFrom] = useState<string | null>(null)
  const [sessionFeatures, setSessionFeatures] = useState<SessionFeatures>({})
  const [sessionCharacter, setSessionCharacter] = useState('')
  const [sessionStory, setSessionStory] = useState('')
  const [sessionMode, setSessionMode] = useState('')
  const [sessionTransmission, setSessionTransmission] = useState('')
  const urlParams = useUrlParams()

  useEffect(() => {
    const unsubStatus = wsService.onStatusChange(setConnected)
    const unsubSession = wsService.on('session', (msg) => {
      const data = msg.data as { requestedSessionId?: string; features?: SessionFeatures; character?: string; story?: string; mode?: string; transmission?: string } | undefined

      if (msg.message) {
        // The server echoes back the session id we asked for. A mismatch means
        // it forked — and the URL rewrite below is exactly what used to hide
        // that — so record it first and let the UI say so.
        const requested = data?.requestedSessionId
        setForkedFrom(requested && requested !== msg.message ? requested : null)

        setSessionId(msg.message)

        const params = new URLSearchParams(window.location.search)
        params.set('session_id', msg.message)
        window.history.replaceState(null, '', `?${params}${window.location.hash}`)
      }
      if (data?.features) {
        setSessionFeatures(data.features)
      }
      if (data?.character !== undefined) setSessionCharacter(data.character)
      if (data?.story !== undefined) setSessionStory(data.story)
      if (data?.mode !== undefined) setSessionMode(data.mode)
      if (data?.transmission !== undefined) setSessionTransmission(data.transmission)
    })

    // WS auth rides on the avataros_session cookie (sent automatically on
    // the upgrade's Cookie header). Convorcher's JWT middleware validates
    // it; the ?api_key= query param is no longer needed.
    wsService.connect({
      sessionId: urlParams.sessionId || sessionId || undefined,
      mode: urlParams.mode,
      transmission: urlParams.transmission,
      character: urlParams.character,
      story: urlParams.story,
      features: urlParams.features,
      fastSentences: urlParams.fastSentences,
    })

    return () => {
      unsubStatus()
      unsubSession()
      wsService.disconnect()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onMessage = useCallback((type: string, handler: (msg: WsMessage) => void) => {
    return wsService.on(type, handler)
  }, [])

  const send = useCallback((msg: object) => wsService.send(msg), [])

  return (
    <WebSocketContext.Provider value={{ connected, sessionId, forkedFrom, sessionFeatures, sessionCharacter, sessionStory, sessionMode, sessionTransmission, send, onMessage }}>
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocket = () => useContext(WebSocketContext)
